import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useBreakpoints } from 'hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan } from 'store/useBusinessToolStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { ReactComponent as AIIconDefault } from 'assets/icons/AIIconDefault.svg';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { Button } from 'components/_basic/Button/Button';
import { CloseButton } from 'components/_basic/CloseButton/CloseButton';
import { ModalComponent } from 'components/_basic/ModalComponent/ModalComponent';
import { TooltipComponent } from 'components/_basic/TooltipComponent';
import { UpgradePlanTooltip } from 'components/_basic/UpgradePlanTooltip';

import style from './SuggestAIGenerationModal.module.scss';

interface ISuggestAIGenerationModalProps {
  handleClose: VoidFunction;
  submitHandler: VoidFunction;
}

export const SuggestAIGenerationModal: FC<ISuggestAIGenerationModalProps> = ({ handleClose, submitHandler }) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();

  const businessPlan = useBusinessToolStore(getBusinessPlan);
  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;

  const isGenerateWholeDocumentDisabled = businessPlan.metadata.isGeneratedWholeDocument && !isStandardOrLightTariff;

  const generateDocumentWithAIHandler = () => {
    if (isGenerateWholeDocumentDisabled) {
      return;
    } else {
      handleClose();
      submitHandler();
    }
  };
  return (
    <ModalComponent open={true} onClose={handleClose}>
      <Box className={style.modalWrapper}>
        <Typography variant="h3" className={style.title}>
          {t('proceedWithAI')} <CloseButton onClick={handleClose} />
        </Typography>
        <Box className={style.content}>
          <Typography component="p" variant="body2">
            {t('enoughInfoForAIGeneration')}
          </Typography>
        </Box>
        <Box className={style.buttonsWrapper}>
          <Button variant="outlined" onClick={handleClose} size="large">
            {t('cancel')}
          </Button>
          <TooltipComponent
            title={<UpgradePlanTooltip />}
            isTooltipHidden={!isGenerateWholeDocumentDisabled}
            trigger={isMobile ? 'click' : 'hover'}
            leaveDelay={isMobile ? 6500 : 200}
          >
            <Button
              size="large"
              variant="contained"
              color="gradient"
              onClick={generateDocumentWithAIHandler}
              className={style.generateAIButton}
            >
              {t('ai.generateWithAI')}
              <AIIconDefault className={style.icon} />
            </Button>
          </TooltipComponent>
        </Box>
      </Box>
    </ModalComponent>
  );
};
